import { useState, useEffect } from "react";
import { useApp } from "@/App";
import { useFetch } from "@/lib/hooks";
import { api } from "@/lib/api";
import type { Item } from "@/lib/types";
import { CATEGORY_LABELS, CONDITION_LABELS } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { FairnessGauge } from "@/components/FairnessGauge";
import { ArrowLeft, ArrowLeftRight, Plus, X, Send, Scale } from "lucide-react";

function TradeItemCard({
	item,
	selected,
	onToggle,
}: {
	item: Item;
	selected: boolean;
	onToggle: () => void;
}) {
	return (
		<div
			onClick={onToggle}
			className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
				selected
					? "border-primary bg-primary/5 ring-1 ring-primary/20"
					: "border-border hover:border-muted-foreground/30"
			}`}
		>
			<div className="flex-1 min-w-0">
				<p className="text-sm font-medium truncate">{item.title}</p>
				<div className="flex items-center gap-2 mt-0.5">
					<span className="text-xs text-muted-foreground">{CATEGORY_LABELS[item.category]}</span>
					<span className="text-xs text-muted-foreground">-</span>
					<span className="text-xs text-muted-foreground">{CONDITION_LABELS[item.condition]}</span>
				</div>
			</div>
			<div className="text-right shrink-0">
				<span className="text-sm font-semibold">${item.estimatedValue}</span>
			</div>
			<div className="shrink-0">
				{selected ? (
					<div className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
						<X className="h-3.5 w-3.5" />
					</div>
				) : (
					<div className="h-6 w-6 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center">
						<Plus className="h-3.5 w-3.5 text-muted-foreground" />
					</div>
				)}
			</div>
		</div>
	);
}

export function TradeBuilderPage({ targetUserId }: { targetUserId: string }) {
	const { navigate, currentUserId } = useApp();
	const { data: myItems } = useFetch(() => api.users.getItems(currentUserId), [currentUserId]);
	const { data: theirItems } = useFetch(() => api.users.getItems(targetUserId), [targetUserId]);
	const { data: targetUser } = useFetch(() => api.users.get(targetUserId), [targetUserId]);

	const [selectedMyItems, setSelectedMyItems] = useState<Set<string>>(new Set());
	const [selectedTheirItems, setSelectedTheirItems] = useState<Set<string>>(new Set());
	const [message, setMessage] = useState("");
	const [fairnessScore, setFairnessScore] = useState<number | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	const toggleMyItem = (id: string) => {
		setSelectedMyItems((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleTheirItem = (id: string) => {
		setSelectedTheirItems((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	useEffect(() => {
		if (selectedMyItems.size > 0 && selectedTheirItems.size > 0) {
			api.fairness
				.calculate(Array.from(selectedMyItems), Array.from(selectedTheirItems))
				.then((res) => setFairnessScore(res.score));
		} else {
			setFairnessScore(null);
		}
	}, [selectedMyItems, selectedTheirItems]);

	const myTotal = (myItems || [])
		.filter((i) => selectedMyItems.has(i.id))
		.reduce((sum, i) => sum + i.estimatedValue, 0);
	const theirTotal = (theirItems || [])
		.filter((i) => selectedTheirItems.has(i.id))
		.reduce((sum, i) => sum + i.estimatedValue, 0);

	const handleSubmit = async () => {
		if (selectedMyItems.size === 0 || selectedTheirItems.size === 0) return;
		setSubmitting(true);
		try {
			await api.swaps.create({
				fromUserId: currentUserId,
				fromUsername: "", // will be filled by server
				toUserId: targetUserId,
				toUsername: targetUser?.username || "",
				offeredItems: Array.from(selectedMyItems),
				requestedItems: Array.from(selectedTheirItems),
				message,
			});
			setSubmitted(true);
		} finally {
			setSubmitting(false);
		}
	};

	if (submitted) {
		return (
			<div className="max-w-2xl mx-auto text-center py-16 space-y-4">
				<div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
					<Send className="h-8 w-8" />
				</div>
				<h2 className="text-2xl font-bold">Swap Proposed!</h2>
				<p className="text-muted-foreground">
					Your trade proposal has been sent to {targetUser?.displayName}. You'll be notified when they respond.
				</p>
				<div className="flex gap-3 justify-center pt-4">
					<Button variant="outline" onClick={() => navigate("/swaps")}>View My Swaps</Button>
					<Button onClick={() => navigate("/browse")}>Browse More</Button>
				</div>
			</div>
		);
	}

	if (currentUserId === targetUserId) {
		return (
			<div className="max-w-2xl mx-auto text-center py-16 space-y-4">
				<p className="text-muted-foreground">You can't trade with yourself.</p>
				<Button onClick={() => navigate("/browse")}>Browse Items</Button>
			</div>
		);
	}

	return (
		<div className="max-w-5xl mx-auto space-y-6">
			<Button variant="ghost" size="sm" onClick={() => navigate("/browse")}>
				<ArrowLeft className="h-4 w-4 mr-1.5" />
				Back
			</Button>

			<div className="flex items-center gap-3">
				<Scale className="h-6 w-6 text-primary" />
				<div>
					<h1 className="text-2xl font-bold tracking-tight">Trade Builder</h1>
					<p className="text-sm text-muted-foreground">
						Build a fair swap with {targetUser?.displayName || "..."}
					</p>
				</div>
			</div>

			<div className="grid lg:grid-cols-[1fr,auto,1fr] gap-6">
				{/* My Items */}
				<Card>
					<CardHeader className="pb-3">
						<div className="flex items-center justify-between">
							<CardTitle className="text-base">Your Items</CardTitle>
							<Badge variant="secondary">{selectedMyItems.size} selected</Badge>
						</div>
						{myTotal > 0 && (
							<p className="text-sm text-muted-foreground">Total: ${myTotal}</p>
						)}
					</CardHeader>
					<CardContent className="space-y-2">
						{myItems?.filter((i) => i.available).map((item) => (
							<TradeItemCard
								key={item.id}
								item={item}
								selected={selectedMyItems.has(item.id)}
								onToggle={() => toggleMyItem(item.id)}
							/>
						))}
						{myItems?.filter((i) => i.available).length === 0 && (
							<p className="text-sm text-muted-foreground text-center py-4">
								No items to trade. <button onClick={() => navigate("/list")} className="text-primary underline">List an item</button>
							</p>
						)}
					</CardContent>
				</Card>

				{/* Fairness Center */}
				<div className="flex lg:flex-col items-center justify-center gap-4 py-4">
					<ArrowLeftRight className="h-8 w-8 text-muted-foreground/50" />
					{fairnessScore !== null && (
						<div className="w-32">
							<FairnessGauge score={fairnessScore} size="md" />
						</div>
					)}
					{fairnessScore === null && (
						<p className="text-xs text-muted-foreground text-center max-w-[120px]">
							Select items from both sides to see fairness score
						</p>
					)}
				</div>

				{/* Their Items */}
				<Card>
					<CardHeader className="pb-3">
						<div className="flex items-center justify-between">
							<CardTitle className="text-base">{targetUser?.displayName}'s Items</CardTitle>
							<Badge variant="secondary">{selectedTheirItems.size} selected</Badge>
						</div>
						{theirTotal > 0 && (
							<p className="text-sm text-muted-foreground">Total: ${theirTotal}</p>
						)}
					</CardHeader>
					<CardContent className="space-y-2">
						{theirItems?.filter((i) => i.available).map((item) => (
							<TradeItemCard
								key={item.id}
								item={item}
								selected={selectedTheirItems.has(item.id)}
								onToggle={() => toggleTheirItem(item.id)}
							/>
						))}
						{theirItems?.filter((i) => i.available).length === 0 && (
							<p className="text-sm text-muted-foreground text-center py-4">
								No available items
							</p>
						)}
					</CardContent>
				</Card>
			</div>

			{/* Message & Submit */}
			<Card>
				<CardContent className="pt-6 space-y-4">
					<div>
						<label className="text-sm font-medium mb-1.5 block">Message (optional)</label>
						<Textarea
							placeholder="Add a message to your swap proposal..."
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							rows={3}
						/>
					</div>

					{fairnessScore !== null && (
						<>
							<Separator />
							<div className="flex items-center justify-between">
								<div className="space-y-1">
									<p className="text-sm font-medium">Trade Summary</p>
									<p className="text-xs text-muted-foreground">
										{selectedMyItems.size} of your items (${myTotal}) for{" "}
										{selectedTheirItems.size} of their items (${theirTotal})
									</p>
								</div>
								<div className="w-24">
									<FairnessGauge score={fairnessScore} size="sm" />
								</div>
							</div>
						</>
					)}

					<Button
						size="lg"
						className="w-full"
						disabled={selectedMyItems.size === 0 || selectedTheirItems.size === 0 || submitting}
						onClick={handleSubmit}
					>
						<Send className="h-4 w-4 mr-2" />
						{submitting ? "Sending..." : "Send Swap Proposal"}
					</Button>
				</CardContent>
			</Card>
		</div>
	);
}
